import { AppState, PlacementMode } from '../state/AppState';
import { BackgroundMusic } from '../audio/BackgroundMusic';
import { LayoutStorage } from '../storage/LayoutStorage';
import { RepositoryLayoutLoader } from '../storage/RepositoryLayoutLoader';
import { NameGenerator } from '../utils/NameGenerator';
import { ShortcutsModal } from '../ui/ShortcutsModal';

type KeyHandler = () => void;

const LONG_PRESS_DURATION = 500; // milliseconds

export class KeyboardHandler {
  private handlers: Map<string, KeyHandler>;
  private appState: AppState;
  private backgroundMusic: BackgroundMusic;
  private xPressStartTime: number | null = null;
  private shortcutsModal: ShortcutsModal | null = null;

  constructor(appState: AppState, backgroundMusic: BackgroundMusic) {
    this.appState = appState;
    this.backgroundMusic = backgroundMusic;
    this.handlers = new Map();
    this.setupHandlers();
    this.attachListeners();
    // Initialize shortcuts modal after DOM is ready
    setTimeout(() => {
      this.shortcutsModal = new ShortcutsModal();
    }, 0);
  }

  private setupHandlers(): void {

    // Construction menu (C) - toggles menu without changing placement mode
    const handleC = () => {
      if (this.appState.mode === PlacementMode.RoadDeleting) {
        this.appState.mode = PlacementMode.View;
      }
      this.toggleConstructionMenu();
    };
    this.handlers.set('c', handleC);
    this.handlers.set('C', handleC);

    // Rotate building or build road (R)
    this.handlers.set('r', () => {
      if (this.appState.mode === PlacementMode.Placing) {
        this.appState.rotateSelectedBuilding();
      } else if (this.appState.mode === PlacementMode.RoadDeleting) {
        // Toggle from delete mode to road placing mode
        this.appState.startRoadPlacing();
      } else if (this.appState.mode !== PlacementMode.RoadPlacing) {
        // Enter road placing mode
        this.appState.startRoadPlacing();
      }
    });
    this.handlers.set('R', () => {
      if (this.appState.mode === PlacementMode.Placing) {
        this.appState.rotateSelectedBuilding();
      } else if (this.appState.mode === PlacementMode.RoadDeleting) {
        // Toggle from delete mode to road placing mode
        this.appState.startRoadPlacing();
      } else if (this.appState.mode !== PlacementMode.RoadPlacing) {
        // Enter road placing mode
        this.appState.startRoadPlacing();
      }
    });

    // Cancel (ESC)
    this.handlers.set('Escape', () => {
      if (this.appState.mode === PlacementMode.Placing) {
        // Cancel placement but keep menu open
        this.appState.cancelPlacement();
        this.clearMenuSelection();
      } else {
        // Exit other modes but leave menu as is
        this.appState.mode = PlacementMode.View;
        this.appState.roadStartPos = null;
        this.clearMenuSelection();
      }
    });

    // Delete mode (X) - one way, only exit with ESC
    // X is handled in keydown/keyup listeners for long-press detection

    // Arrow keys for sector navigation
    this.handlers.set('ArrowLeft', () => {
      this.navigateToAdjacentSector('left');
    });

    this.handlers.set('ArrowRight', () => {
      this.navigateToAdjacentSector('right');
    });

    // Q/E as aliases for left/right sector navigation
    this.handlers.set('q', () => {
      this.navigateToAdjacentSector('left');
    });
    this.handlers.set('Q', () => {
      this.navigateToAdjacentSector('left');
    });
    this.handlers.set('e', () => {
      this.navigateToAdjacentSector('right');
    });
    this.handlers.set('E', () => {
      this.navigateToAdjacentSector('right');
    });

    // Mute/unmute background music (M)
    this.handlers.set('m', () => {
      this.backgroundMusic.toggleMute();
    });
    this.handlers.set('M', () => {
      this.backgroundMusic.toggleMute();
    });

    // Toggle inactive adjacency indicators (T)
    this.handlers.set('t', () => {
      this.appState.showInactiveIndicators = !this.appState.showInactiveIndicators;
    });
    this.handlers.set('T', () => {
      this.appState.showInactiveIndicators = !this.appState.showInactiveIndicators;
    });

    // Save layout (S)
    this.handlers.set('s', () => {
      this.showSaveLayoutModal();
    });
    this.handlers.set('S', () => {
      this.showSaveLayoutModal();
    });

    // Load layout (L)
    this.handlers.set('l', () => {
      this.showLoadLayoutModal();
    });
    this.handlers.set('L', () => {
      this.showLoadLayoutModal();
    });

    // Undo last placement (Z)
    this.handlers.set('z', () => {
      this.appState.undo();
    });
    this.handlers.set('Z', () => {
      this.appState.undo();
    });

    // Shortcuts modal (?)
    this.handlers.set('?', () => {
      if (this.shortcutsModal) {
        this.shortcutsModal.toggle();
      }
    });
  }

  private attachListeners(): void {
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      // Handle shortcuts modal: visual feedback and ESC close
      if (this.shortcutsModal?.getIsOpen()) {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.shortcutsModal.close();
          return;
        }
        // Provide visual feedback for keypresses
        this.shortcutsModal.updateKeyVisualFeedback(e.key, true);
        return;
      }

      // If a modal is open, let its own handler deal with it, but stop further propagation.
      if (this.appState.isModalOpen) {
        e.stopImmediatePropagation();
        return;
      }

      // Skip if any modifier keys are pressed
      if (e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }

      // Track X press for long-press clear sector
      if ((e.key === 'x' || e.key === 'X') && !this.xPressStartTime) {
        this.xPressStartTime = Date.now();
        // Don't execute handler yet, wait to see if it's a long press
        e.preventDefault();
        return;
      }

      const handler = this.handlers.get(e.key);
      if (handler) {
        e.preventDefault();
        handler();
      }
    });

    document.addEventListener('keyup', (e: KeyboardEvent) => {
      // Clear visual feedback in shortcuts modal
      if (this.shortcutsModal?.getIsOpen()) {
        this.shortcutsModal.updateKeyVisualFeedback(e.key, false);
        return;
      }

      // If a modal is open, do nothing.
      if (this.appState.isModalOpen) {
        return;
      }

      if (e.key === 'x' || e.key === 'X') {
        if (this.xPressStartTime) {
          const pressDuration = Date.now() - this.xPressStartTime;
          if (pressDuration >= LONG_PRESS_DURATION) {
            // Long press: show confirmation modal for clearing sectors with options
            this.showConfirmationModal(
              'Clear Sector',
              'What would you like to clear?',
              undefined,
              {
                cancel: () => {
                  // Do nothing, just close the modal
                },
                current: () => {
                  this.appState.clearCurrentSector();
                },
                all: () => {
                  this.appState.clearAllSectors();
                }
              }
            );
          } else {
            // Short press: toggle to delete mode or between road modes
            if (this.appState.mode === PlacementMode.RoadPlacing) {
              // Toggle from road placing to road deleting
              this.appState.startRoadDeleting();
            } else if (this.appState.mode !== PlacementMode.RoadDeleting && this.appState.mode !== PlacementMode.Placing) {
              // Enter delete mode
              this.appState.startRoadDeleting();
            }
          }
          this.xPressStartTime = null;
        }
      }
    });
  }

  private showConfirmationModal(
    title: string,
    message: string,
    onConfirm?: () => void,
    buttons?: { cancel?: () => void; current?: () => void; all?: () => void }
  ): void {
    this.appState.isModalOpen = true;
    const modal = document.getElementById('confirmation-modal');
    const modalTitle = document.getElementById('confirmation-title');
    const modalMessage = document.getElementById('confirmation-message');
    const confirmYes = document.getElementById('confirm-yes');
    const confirmCancel = document.getElementById('confirm-cancel');
    const confirmCurrent = document.getElementById('confirm-current');
    const confirmAll = document.getElementById('confirm-all');

    if (!modal || !modalTitle || !modalMessage || !confirmYes || !confirmCancel || !confirmCurrent || !confirmAll) {
      this.appState.isModalOpen = false;
      return;
    }

    modalTitle.textContent = title;
    modalMessage.textContent = message;

    // Determine which mode we're in (legacy Yes/No or sector clearing)
    const isSectorMode = buttons !== undefined;

    // Hide all buttons initially
    confirmYes.classList.add('hidden');
    confirmCancel.classList.add('hidden');
    confirmCurrent.classList.add('hidden');
    confirmAll.classList.add('hidden');

    // Define handlers
    const handleCancel = () => {
      buttons?.cancel?.();
      closeModal();
    };

    const handleCurrent = () => {
      buttons?.current?.();
      closeModal();
    };

    const handleAll = () => {
      buttons?.all?.();
      closeModal();
    };

    const handleYes = () => {
      onConfirm?.();
      closeModal();
    };

    const handleNo = () => {
      closeModal();
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeModal();
      }
    };

    const closeModal = () => {
      this.appState.isModalOpen = false;
      modal.classList.add('hidden');
      // Remove all event listeners
      confirmCancel.removeEventListener('click', handleCancel);
      confirmCancel.removeEventListener('click', handleNo);
      confirmCurrent.removeEventListener('click', handleCurrent);
      confirmAll.removeEventListener('click', handleAll);
      confirmYes.removeEventListener('click', handleYes);
      document.removeEventListener('keydown', handleEscape, true);
    };

    if (isSectorMode && buttons) {
      // Sector clearing mode: show Cancel, Current Sector, All Sectors
      confirmCancel.classList.remove('hidden');
      confirmCurrent.classList.remove('hidden');
      confirmAll.classList.remove('hidden');

      confirmCancel.addEventListener('click', handleCancel);
      confirmCurrent.addEventListener('click', handleCurrent);
      confirmAll.addEventListener('click', handleAll);
    } else {
      // Legacy mode: show Cancel and Yes
      confirmCancel.classList.remove('hidden');
      confirmYes.classList.remove('hidden');

      confirmCancel.addEventListener('click', handleNo);
      confirmYes.addEventListener('click', handleYes);
    }

    document.addEventListener('keydown', handleEscape, true); // Use capture phase
    modal.classList.remove('hidden');
  }

  private toggleConstructionMenu(): void {
    const menu = document.getElementById('construction-menu');
    if (menu?.classList.contains('hidden')) {
      menu.classList.remove('hidden');
    } else {
      menu?.classList.add('hidden');
    }
  }

  private closeConstructionMenu(): void {
    const menu = document.getElementById('construction-menu');
    menu?.classList.add('hidden');
  }

  private clearMenuSelection(): void {
    document.querySelectorAll('.building-item.selected').forEach(el => {
      el.classList.remove('selected');
    });
  }

  private navigateToAdjacentSector(direction: 'left' | 'right'): void {
    const currentSector = this.appState.getSector();
    const nextSector =
      direction === 'left'
        ? currentSector === 1 ? 6 : currentSector - 1
        : currentSector === 6 ? 1 : currentSector + 1;
    this.appState.switchSector(nextSector);
  }

  private showSaveLayoutModal(): void {
    this.appState.isModalOpen = true;
    const modal = document.getElementById('save-layout-modal');
    const input = document.getElementById('layout-name-input') as HTMLInputElement;
    const cancelBtn = document.getElementById('save-cancel');
    const confirmBtn = document.getElementById('save-confirm');

    if (!modal || !input || !cancelBtn || !confirmBtn) {
      this.appState.isModalOpen = false;
      return;
    }

    const defaultName = NameGenerator.generate();
    input.value = '';
    input.placeholder = defaultName;
    input.focus(); // Autofocus the input

    const handleConfirm = () => {
      const name = input.value.trim() || defaultName;
      if (name) {
        this.appState.saveLayout(name);
        closeModal();
      }
    };

    const handleCancel = () => {
      closeModal();
    };

    const closeModal = () => {
      this.appState.isModalOpen = false;
      modal.classList.add('hidden');
      cancelBtn.removeEventListener('click', handleCancel);
      confirmBtn.removeEventListener('click', handleConfirm);
      document.removeEventListener('keydown', handleKeydown, true);
    };

    const handleKeydown = (e: KeyboardEvent) => {
      e.stopPropagation(); // Stop event from bubbling to global listener
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'Enter') {
        handleConfirm();
      }
    };

    cancelBtn.addEventListener('click', handleCancel);
    confirmBtn.addEventListener('click', handleConfirm);
    document.addEventListener('keydown', handleKeydown, true); // Use capture phase

    modal.classList.remove('hidden');
    input.focus(); // Re-focus just in case
  }

  private showLoadLayoutModal(): void {
    this.appState.isModalOpen = true;
    const modal = document.getElementById('load-layout-modal');
    const listDiv = document.getElementById('layouts-list');
    const closeBtn = document.getElementById('load-close');

    if (!modal || !listDiv || !closeBtn) {
      this.appState.isModalOpen = false;
      return;
    }

    const renderList = async () => {
      listDiv.innerHTML = '';

      // Load both repository and user layouts
      const repoLayouts = await RepositoryLayoutLoader.loadAllLayouts();
      const userLayouts = LayoutStorage.getAllLayouts();

      // Sort repository layouts alphabetically
      repoLayouts.sort((a, b) => a.metadata.name.localeCompare(b.metadata.name));

      // Sort user layouts by timestamp (newest first)
      userLayouts.sort((a, b) => (b.metadata.timestamp ?? 0) - (a.metadata.timestamp ?? 0));

      const hasRepoLayouts = repoLayouts.length > 0;
      const hasUserLayouts = userLayouts.length > 0;

      if (!hasRepoLayouts && !hasUserLayouts) {
        const empty = document.createElement('div');
        empty.className = 'empty-message';
        empty.textContent = 'No layouts available';
        listDiv.appendChild(empty);
        return;
      }

      // Render repository layouts section
      if (hasRepoLayouts) {
        const repoHeader = document.createElement('div');
        repoHeader.className = 'layout-section-header';
        repoHeader.textContent = 'REPOSITORY LAYOUTS (Read-Only)';
        listDiv.appendChild(repoHeader);

        for (const layout of repoLayouts) {
          this.createLayoutItem(layout, true, listDiv, closeModal);
        }
      }

      // Render user layouts section
      if (hasUserLayouts) {
        const userHeader = document.createElement('div');
        userHeader.className = 'layout-section-header';
        userHeader.textContent = 'YOUR LAYOUTS';
        listDiv.appendChild(userHeader);

        for (const layout of userLayouts) {
          this.createLayoutItem(layout, false, listDiv, closeModal, () => renderList());
        }
      }
    };

    const handleClose = () => {
      closeModal();
    };

    const closeModal = () => {
      this.appState.isModalOpen = false;
      modal.classList.add('hidden');
      closeBtn.removeEventListener('click', handleClose);
      document.removeEventListener('keydown', handleKeydown, true);
    };

    const handleKeydown = (e: KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    closeBtn.addEventListener('click', handleClose);
    document.addEventListener('keydown', handleKeydown, true); // Use capture phase

    renderList();
    modal.classList.remove('hidden');
  }

  private createLayoutItem(
    layout: any,
    isRepository: boolean,
    container: HTMLElement,
    closeModal: () => void,
    onUpdate?: () => void
  ): void {
    const item = document.createElement('div');
    item.className = `layout-item ${isRepository ? 'repository' : ''}`;

    const info = document.createElement('div');
    info.className = 'layout-info';

    const name = document.createElement('div');
    name.className = 'layout-name';
    name.textContent = layout.metadata.name;

    info.appendChild(name);

    // Only show date if timestamp exists
    if (layout.metadata.timestamp) {
      const date = document.createElement('div');
      date.className = 'layout-date';
      date.textContent = new Date(layout.metadata.timestamp).toLocaleString();
      info.appendChild(date);
    }

    const actions = document.createElement('div');
    actions.className = 'layout-actions';

    // Add reference button if URL exists (on the left)
    if (layout.metadata.referenceUrl) {
      const referenceBtn = document.createElement('button');
      referenceBtn.className = 'layout-button layout-reference';
      referenceBtn.textContent = 'Reference';
      referenceBtn.addEventListener('click', () => {
        window.open(layout.metadata.referenceUrl, '_blank');
      });
      actions.appendChild(referenceBtn);
    }

    const loadBtn = document.createElement('button');
    loadBtn.className = 'layout-button layout-load';
    loadBtn.textContent = 'Load';
    loadBtn.addEventListener('click', () => {
      closeModal();
      this.loadLayoutWithConfirmation(layout.metadata.id, layout);
    });
    actions.appendChild(loadBtn);

    if (!isRepository) {
      const exportBtn = document.createElement('button');
      exportBtn.className = 'layout-button layout-export';
      exportBtn.textContent = 'Export';
      exportBtn.addEventListener('click', () => {
        LayoutStorage.exportLayoutAsJSON(layout.metadata.id);
      });
      actions.appendChild(exportBtn);

      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'layout-button layout-delete';
      deleteBtn.textContent = 'Delete';
      deleteBtn.addEventListener('click', () => {
        LayoutStorage.deleteLayout(layout.metadata.id);
        if (onUpdate) onUpdate();
      });
      actions.appendChild(deleteBtn);
    }

    item.appendChild(info);
    item.appendChild(actions);

    container.appendChild(item);
  }

  private loadLayoutWithConfirmation(layoutId: string, layoutData?: any): void {
    this.showConfirmationModal(
      'Load Layout',
      'Load this layout? This will replace your current layout.',
      () => {
        if (layoutData) {
          // For repository layouts or when we have the full layout object
          this.appState.loadStationData(layoutData.data);
        } else {
          // For user layouts stored in localStorage
          this.appState.loadLayout(layoutId);
        }
      }
    );
  }
}
